import { useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";
import { PRIVATE_LESSON_RECOVERY_EVENT, type PrivateLessonRecoveryStatus } from "./privateLessonDraft.js";

export function PwaUpdatePrompt() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [applyUpdate, setApplyUpdate] = useState<(() => Promise<void>) | null>(null);
  const [draftRecovery, setDraftRecovery] = useState<PrivateLessonRecoveryStatus>({ dirty: false, persisted: true });

  useEffect(() => {
    const updateServiceWorker = registerSW({
      immediate: true,
      onNeedRefresh: () => setUpdateAvailable(true),
    });
    setApplyUpdate(() => updateServiceWorker);
  }, []);

  useEffect(() => {
    const onRecoveryStatus = (event: Event) => {
      const status = (event as CustomEvent<PrivateLessonRecoveryStatus>).detail;
      if (status && typeof status.dirty === "boolean" && typeof status.persisted === "boolean") setDraftRecovery(status);
    };
    window.addEventListener(PRIVATE_LESSON_RECOVERY_EVENT, onRecoveryStatus);
    return () => window.removeEventListener(PRIVATE_LESSON_RECOVERY_EVENT, onRecoveryStatus);
  }, []);

  if (!updateAvailable) return null;

  const update = async () => {
    if (!applyUpdate) return;
    if (draftRecovery.dirty && !draftRecovery.persisted) {
      setProblem("A private lesson draft could not be saved in this tab. Copy the changes or choose Later before updating.");
      return;
    }
    setProblem(null);
    setUpdating(true);
    try {
      // The registration helper tells the waiting worker to activate and
      // reloads when it takes control, keeping lazy route chunks in one build.
      await applyUpdate();
    } catch {
      setProblem("The update could not be applied. Please try again.");
      setUpdating(false);
    }
  };

  return (
    <aside className="pwa-update-prompt" aria-label="WMF Hub update" role="status" aria-live="polite" data-bottom-obstruction="prompt">
      <span className="pwa-update-copy">
        <strong>WMF Hub update ready</strong>
        <span>Refresh now to use the latest version.</span>
        {problem && <span className="pwa-update-error" role="alert">{problem}</span>}
      </span>
      <span className="pwa-update-actions">
        <button className="primary" type="button" disabled={updating} onClick={() => void update()}>
          {updating ? "Updating…" : "Update"}
        </button>
        <button className="quiet" type="button" disabled={updating} onClick={() => setUpdateAvailable(false)}>
          Later
        </button>
      </span>
    </aside>
  );
}
